import { Carousel, Descriptions, Divider, Image, Tag, Typography } from "antd";
import {
  MapPin,
  Package,
  Ruler,
  Scale,
  Truck,
  X,
} from "lucide-react";
import React, { useState } from "react";
import { TInventory, TServiceTicket, TBooking } from "~/types";
import { capitalize, formatDimensions, formatDate } from "~/utils";
import { ContainerType, InventoryStatusCode, PackingTypes } from "~/utils/constants";
import HistoryServiceTickets from "./history-service-tickets";
import { ServiceDetailSidebar } from "~/components/client/bookings/service-detail-sidebar";
import "./inventory.css";

const { Text, Title } = Typography;

interface InventoryDetailSidebarProps {
  inventory: TInventory | null;
  onClose: () => void;
}

const getStatusColor = (status?: InventoryStatusCode) => {
  switch (status) {
    case InventoryStatusCode.AVAILABLE_LIVE:
      return "green";
    case InventoryStatusCode.AVAILABLE_ON_HAND:
      return "blue";
    case InventoryStatusCode.NO_POSSESSION:
      return "orange";
    case InventoryStatusCode.RELEASED:
      return "purple";
    default:
      return "default";
  }
};

const formatPackingType = (packingType?: PackingTypes, containerType?: ContainerType) => {
  if (containerType) {
    const containerMap: Record<ContainerType, string> = {
      [ContainerType.CRATE]: "Crate",
      [ContainerType.PACKAGE]: "Package",
      [ContainerType.PIECE]: "Piece",
      [ContainerType.T_Frame]: "T Frame",
      [ContainerType.SLIPCASE]: "Slipcase",
      [ContainerType.BOX]: "Box",
      [ContainerType.CUSTOM]: "Custom",
      [ContainerType.SLATCRATE]: "Slat Crate",
      [ContainerType.CBIN]: "C-Bin",
      [ContainerType.PALLET]: "Pallet",
    };
    return containerMap[containerType] || containerType;
  }
  if (!packingType) return null;
  return capitalize(packingType.replace(/_/g, " "));
};

const formatStatusText = (status?: InventoryStatusCode) => {
  const statusText = capitalize(status || "");
  if (statusText === "No Possession") {
    return "No AHC Possession";
  }
  return statusText;
};

export const InventoryDetailSidebar: React.FC<InventoryDetailSidebarProps> = ({
  inventory,
  onClose,
}) => {
  const [selectedService, setSelectedService] = useState<TServiceTicket | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<TBooking | null>(null);

  if (!inventory) return null;

  const hasImages = inventory.images && inventory.images.length > 0;

  const handleServiceClick = (service: TServiceTicket, booking?: TBooking) => {
    setSelectedService(service);
    setSelectedBooking(booking || null);
  };

  const handleCloseServiceSidebar = () => {
    setSelectedService(null);
    setSelectedBooking(null);
  };

  const isServiceSidebarOpen = selectedService !== null;

  return (
    <div className="h-full flex relative overflow-visible">
      <div className="h-full flex flex-col bg-[#fffff5] border-l-2 border-black flex-1 overflow-visible">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b-2 border-black bg-[#fffff5]">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Package size={20} className="text-neutral-500 flex-shrink-0" />
          <div className="min-w-0">
            <Title level={5} className="!mb-0 truncate">
              {inventory.inventoryRef || "Inventory Details"}
            </Title>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-neutral-200 rounded transition-colors flex-shrink-0 ml-2"
        >
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto">
        {/* Image Carousel */}
        {hasImages && (
          <div className="bg-[#fffff5] p-4 border-b-2 border-black">
            <div className="inventory-carousel bg-[#fffff5] border-2 border-black overflow-hidden">
              <Carousel autoplay autoplaySpeed={4000} arrows>
                {inventory.images.map((image, index) => (
                  <div key={image.id || index}>
                    <div className="h-64 flex items-center justify-center bg-[#fffff5]">
                      <Image
                        src={image.url}
                        alt={`${inventory.title} - Image ${index + 1}`}
                        className="max-h-64 object-contain"
                        style={{ maxHeight: "256px" }}
                      />
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
            <Text type="secondary" className="text-xs mt-2 block text-center">
              {inventory.images.length} image{inventory.images.length > 1 ? "s" : ""}
            </Text>
          </div>
        )}

        <div className="p-4 space-y-6">
          {/* Title & Status */}
          <div>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <Title level={4} className="!mb-1">
                  {inventory.title || "Untitled"}
                </Title>
                {inventory.artistName && (
                  <Text type="secondary">by {inventory.artistName}</Text>
                )}
              </div>
              <Tag color={getStatusColor(inventory.status)} className="flex-shrink-0">
                {formatStatusText(inventory.status)}
              </Tag>
            </div>
          </div>

          <Divider className="!my-4" />

          {/* Basic Info */}
          <div>
            <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
              Basic Information
            </Text>
            <Descriptions column={1} size="small" className="mt-2">
              <Descriptions.Item label="Reference">
                <Text strong>{inventory.inventoryRef || "-"}</Text>
              </Descriptions.Item>
              {inventory.medium && (
                <Descriptions.Item label="Medium">
                  {inventory.medium}
                </Descriptions.Item>
              )}
              {inventory.clientInternalRef && (
                <Descriptions.Item label="Client Ref">
                  {inventory.clientInternalRef}
                </Descriptions.Item>
              )}
            </Descriptions>
          </div>

          <Divider className="!my-4" />

          {/* Dimensions & Physical */}
          <div>
            <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
              Physical Details
            </Text>
            <div className="mt-3 space-y-3">
              <div className="flex items-center gap-3 p-3 bg-[#fffff5] border-2 border-black">
                <Ruler size={18} className="text-black" />
                <div>
                  <Text type="secondary" className="text-xs block">Dimensions</Text>
                  <Text strong>{formatDimensions(inventory)}</Text>
                </div>
              </div>

              {inventory.weight > 0 && (
                <div className="flex items-center gap-3 p-3 bg-[#fffff5] border-2 border-black">
                  <Scale size={18} className="text-black" />
                  <div>
                    <Text type="secondary" className="text-xs block">Weight</Text>
                    <Text strong>{inventory.weight} lbs</Text>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {formatPackingType(inventory.packingType, inventory.containerType) && (
                  <div className="p-3 bg-[#fffff5] border-2 border-black">
                    <Text type="secondary" className="text-xs block">Packing</Text>
                    <Text strong className="text-sm">
                      {formatPackingType(inventory.packingType, inventory.containerType)}
                    </Text>
                  </div>
                )}
                {inventory.parts && inventory.parts > 1 && (
                  <div className="p-3 bg-[#fffff5] border-2 border-black">
                    <Text type="secondary" className="text-xs block">Parts</Text>
                    <Text strong className="text-sm">{inventory.parts}</Text>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Location */}
          {inventory.status === InventoryStatusCode.AVAILABLE_ON_HAND && inventory.location && (
            <>
              <Divider className="!my-4" />
              <div>
                <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
                  Current Location
                </Text>
                <div className="mt-2 p-3 bg-[#fffff5] border-2 border-black">
                  <div className="flex items-start gap-2">
                    <MapPin size={16} className="text-blue-500 mt-0.5" />
                    <div>
                      <Text strong className="block text-blue-900">
                        {inventory.location.name || "Storage Location"}
                      </Text>
                      {inventory.location.locationRef && (
                        <Text type="secondary" className="text-sm">
                          Ref: {inventory.location.locationRef}
                        </Text>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Value */}
          {inventory.value && inventory.value > 0 && (
            <>
              <Divider className="!my-4" />
              <div>
                <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
                  Value
                </Text>
                <div className="mt-2 p-3 bg-[#fffff5] border-2 border-black">
                  <Text strong className="text-lg">
                    ${inventory.value.toLocaleString()}
                  </Text>
                </div>
              </div>
            </>
          )}

          {/* Special Handling */}
          {inventory.specialHandling && inventory.specialHandling.length > 0 && (
            <>
              <Divider className="!my-4" />
              <div>
                <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
                  Special Handling
                </Text>
                <div className="mt-2 flex flex-wrap gap-2">
                  {inventory.specialHandling.map((handling, idx) => (
                    <Tag key={idx} color="orange">
                      {handling}
                    </Tag>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Description */}
          {inventory.description && (
            <>
              <Divider className="!my-4" />
              <div>
                <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
                  Description
                </Text>
                <div className="mt-2 p-3 bg-[#fffff5] border-2 border-black">
                  <Text>{inventory.description}</Text>
                </div>
              </div>
            </>
          )}

          {/* Notes */}
          {inventory.notes && (
            <>
              <Divider className="!my-4" />
              <div>
                <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
                  Notes
                </Text>
                <div className="mt-2 p-3 bg-[#fffff5] border-2 border-black">
                  <Text>{inventory.notes}</Text>
                </div>
              </div>
            </>
          )}

          {/* Service Tickets */}
          <Divider className="!my-4" />
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Truck size={16} className="text-neutral-500" />
              <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
                Service Tickets
              </Text>
            </div>
            <HistoryServiceTickets 
              inventoryId={inventory.id} 
              onServiceClick={handleServiceClick}
            />
          </div>

          {/* Metadata */}
          <Divider className="!my-4" />
          <div>
            <Text strong className="text-neutral-500 text-xs uppercase tracking-wide">
              Record Info
            </Text>
            <Descriptions column={1} size="small" className="mt-2">
              <Descriptions.Item label="Created">
                {formatDate(inventory.createdAt)}
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {formatDate(inventory.updatedAt)}
              </Descriptions.Item>
            </Descriptions>
          </div>
        </div>
      </div>
      </div>

      {/* Service Detail Sidebar (nested) - appears to the left of inventory sidebar */}
      {isServiceSidebarOpen && selectedService && (
        <>
          <div
            className={`absolute right-full top-0 h-full bg-[#fffff5] transition-all duration-300 ease-in-out border-r-2 border-black w-[400px] translate-x-0`}
            style={{ zIndex: 60 }}
          >
            <ServiceDetailSidebar
              service={selectedService}
              booking={selectedBooking}
              onClose={handleCloseServiceSidebar}
            />
          </div>
          {/* Overlay for nested sidebar on mobile */}
          <div
            className="fixed inset-0 bg-black bg-opacity-25 z-[45] lg:hidden pointer-events-auto"
            onClick={handleCloseServiceSidebar}
          />
        </>
      )}
    </div>
  );
};
